import { useState } from 'react';

function fmt(value, currency = 'BRL') {
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  return parseFloat(value || 0).toLocaleString(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
}

const MONTH_LABELS = { 12: '1 yr', 24: '2 yrs', 60: '5 yrs' };

const SIM_COLORS = ['text-sky-400', 'text-violet-400', 'text-amber-400', 'text-rose-400', 'text-emerald-400'];
const SIM_ACTIVE_BTN = [
  'border-sky-700/60 bg-sky-900/20 text-sky-400',
  'border-violet-700/60 bg-violet-900/20 text-violet-400',
  'border-amber-700/60 bg-amber-900/20 text-amber-400',
  'border-rose-700/60 bg-rose-900/20 text-rose-400',
  'border-emerald-700/60 bg-emerald-900/20 text-emerald-400',
];
const SIM_CARD_BORDER = [
  'border-sky-700/50',
  'border-violet-700/50',
  'border-amber-700/50',
  'border-rose-700/50',
  'border-emerald-700/50',
];

function InstrumentCard({ instrument, currency, simulations, onToggle, maxSims, paid }) {
  const [open, setOpen] = useState(false);
  const activeIdx = simulations.findIndex((s) => s.name === instrument.name);
  const isActive = activeIdx !== -1;
  const atLimit = !isActive && simulations.length >= maxSims;
  const freeBlocked = atLimit && !paid;

  return (
    <div className={`rounded-xl bg-surface-card overflow-hidden border transition-colors ${
      isActive ? SIM_CARD_BORDER[activeIdx % SIM_CARD_BORDER.length] : 'border-surface'
    }`}>
      <div className="flex items-center px-5 py-4 gap-3">
        {/* Expand area — left */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex-1 flex items-center gap-3 text-left min-w-0"
        >
          <span className="text-lg shrink-0">📈</span>
          <div className="min-w-0">
            <p className="font-semibold text-slate-200 text-sm truncate">{instrument.name}</p>
            <p className="text-xs text-slate-500">{instrument.type}</p>
          </div>
        </button>

        {/* Controls — right */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => onToggle(instrument)}
            disabled={atLimit}
            title={
              freeBlocked
                ? 'Upgrade to Pro to run multiple simulations at once'
                : atLimit
                  ? `Max ${maxSims} simulations active. Remove one first.`
                  : isActive ? 'Remove from table' : 'Simulate in projection table'
            }
            className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
              isActive
                ? SIM_ACTIVE_BTN[activeIdx % SIM_ACTIVE_BTN.length]
                : atLimit
                  ? 'border-surface text-slate-600 cursor-not-allowed'
                  : 'border-surface text-slate-400 hover:border-brand-600 hover:text-brand-400'
            }`}
          >
            {freeBlocked ? '🔒 Pro' : isActive ? '✕ Simulating' : '📊 Simulate'}
          </button>

          <div className="text-right">
            <p className={`font-bold text-sm ${
              isActive ? SIM_COLORS[activeIdx % SIM_COLORS.length] : 'text-brand-400'
            }`}>
              {instrument.annual_rate_pct}% /yr
            </p>
            <p className="text-slate-500 text-xs">{instrument.monthly_rate_pct?.toFixed(2)}% /mo</p>
          </div>

          <button onClick={() => setOpen((v) => !v)} className="text-slate-500 text-xs w-4 text-center">
            {open ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-surface">
          <p className="text-slate-400 text-sm leading-relaxed pt-3">{instrument.description}</p>

          {instrument.tax_notes && (
            <div className="flex gap-2 bg-amber-950/30 border border-amber-800/40 rounded-lg px-4 py-3">
              <span className="text-amber-400 text-xs mt-0.5">⚠</span>
              <p className="text-amber-300 text-xs leading-relaxed">{instrument.tax_notes}</p>
            </div>
          )}

          {instrument.growth_projections?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Capital Growth Projections
              </p>
              <div className="grid grid-cols-3 gap-3">
                {instrument.growth_projections.map((p) => (
                  <div
                    key={p.months}
                    className="rounded-lg bg-surface/60 border border-surface px-3 py-3 text-center"
                  >
                    <p className="text-xs text-slate-500 mb-1">{MONTH_LABELS[p.months] || `${p.months}mo`}</p>
                    <p className="text-slate-100 font-bold text-sm">{fmt(p.final_value, currency)}</p>
                    <p className="text-green-400 text-xs mt-0.5">+{fmt(p.interest_earned, currency)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function InvestmentEducation({ education, currency, simulations = [], onToggleSimulation, maxSims = 3, paid = false }) {
  if (!education || !education.instruments?.length) return null;

  const freeAtLimit = !paid && simulations.length >= maxSims;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-xl">🏦</span>
          <h2 className="text-lg font-bold text-slate-100">Investment Instruments</h2>
        </div>
        {simulations.length > 0 && (
          <p className="text-xs text-slate-500">
            {simulations.length}/{maxSims} simulations active &mdash; scroll up to see the table
          </p>
        )}
        {freeAtLimit && (
          <a href="/pricing" className="text-xs btn-ghost text-amber-400 flex items-center gap-1">
            🔒 Upgrade for multiple simulations
          </a>
        )}
      </div>

      {/* Disclaimer */}
      <div className="rounded-xl border border-blue-800/40 bg-blue-950/20 px-5 py-4 flex gap-3">
        <span className="text-blue-400 text-sm mt-0.5">ℹ</span>
        <p className="text-blue-300 text-xs leading-relaxed">{education.disclaimer}</p>
      </div>

      {/* Country context */}
      {education.country_context && (
        <p className="text-slate-400 text-sm leading-relaxed">{education.country_context}</p>
      )}

      {/* Available capital note */}
      {education.available_capital > 0 && (
        <p className="text-xs text-slate-500">
          Projections below are based on an initial capital of{' '}
          <span className="text-slate-300 font-medium">{fmt(education.available_capital, currency)}</span>.
        </p>
      )}

      {/* Instrument cards */}
      <div className="space-y-3">
        {education.instruments.map((inst, i) => (
          <InstrumentCard
            key={i}
            instrument={inst}
            currency={currency}
            simulations={simulations}
            onToggle={onToggleSimulation}
            maxSims={maxSims}
            paid={paid}
          />
        ))}
      </div>
    </div>
  );
}
